import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Result, SessionProvider, Validator, ValidationErrors, handleServerErrors } from 'core/infrastructure';
import { Res, DocumentEmailOptionsDTO, SendDocumentEmailResultDTO } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

type Props = DocumentEmailOptionsDTO & Modal.Actions<DocumentEmailOptionsDTO>;
type State = DocumentEmailOptionsDTO;

class SendDocumentEmail extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();

	protected getScreenId(): string {
		return 'SEND_DOCUMENT_EMAIL';
	}

	protected getTitle(): string {
		return Res.Web.SEND_DOCUMENT_EMAIL();
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getImage(): string | SvgIcon {
		return Images.SendEmail;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({ onOK: this.onOK });
	}

	protected useKeypad(): boolean {
		return false;
	}

	createInitialState(props: Props): Nullable<DocumentEmailOptionsDTO> {
		return { ...props };
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	onOK = async () => {
		const ok = await this.form.current!.updateModel();
		if (ok)
			this.close(this.state);
	};

	protected getContent(): React.ReactNode {
		const { Form, ComboBox, TextBox } = UI.controlsFor<State>();

		const validate = (state: State) => {
			const errors: ValidationErrors = {};

			if (state.Template === null || state.Template.Id === 0)
				ValidationErrors.add(errors, 'Template', Res.Msg.MSG_INVOICE_TEMPLATE_MUST_BE_SELECTED_TO_SEND_AN_EMAIL());

			if (state.Email === null || state.Email === '')
				ValidationErrors.add(errors, 'Email', Res.Msg.MSG_EMAIL_MUST_BE_INTRODUCED());

			return errors;
		};

		return <Form ref={this.form} model={this.state} validator={Validator.create(validate)} className='m-sm'>
			<Cols>
				<Col width='*'>
					<ComboBox linkTo={x => x.Template} options={this.props.ExistingTemplates} label={Res.Web.SEND_DOCUMENT_EMAIL_TEMPLATES_LBL()} />
					<TextBox linkTo={x => x.Email} label={Res.Web.SEND_DOCUMENT_EMAIL_TO_LBL()} />
					<TextBox linkTo={x => x.Subject} label={Res.Web.SEND_DOCUMENT_EMAIL_SUBJECT_LBL()} />
					<TextBox linkTo={x => x.Body} label={Res.Web.SEND_DOCUMENT_EMAIL_BODY_LBL()} lines={8} />
				</Col>
			</Cols>
		</Form>;
	}

}

async function handleRequest<T>(fn: () => Promise<T>): Promise<Result<T>> {
	return await handleServerErrors(fn, {
		onServerError: async (error) => {
			await Message.show(error.message);
		}
	});
}

type Context = {
	getDefaultOptions(): DocumentEmailOptionsDTO;
	send(options: DocumentEmailOptionsDTO): Promise<SendDocumentEmailResultDTO>
}

export default async function sendDocumentEmail(context: Context): Promise<Result> {

	if (!SessionProvider.isEmailingModuleEnabled()) {
		await Message.show(Res.Msg.MSG_EMAILING_MODULE_IS_DISABLED());
		return Result.error();
	}

	if (!context.getDefaultOptions().HasEmailAccount) {
		await Message.show(Res.Msg.MSG_WORKPLACE_HAS_NOT_EMAIL_ACCOUNT());
		return Result.error();
	}

	const result = await Modal.show(SendDocumentEmail, { ...context.getDefaultOptions() });

	if (!result.ok)
		return Result.error();

	const handledResult = await handleRequest(async () => context.send(result.value));

	if (!handledResult.ok)
		return Result.error();

	const sendResult = handledResult.value;

	if (sendResult.Error) {
		await Message.show(sendResult.Error);
		return Result.error();
	}

	return Result.ok();
}
